import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { openOverlayScrim, scrimColorOf } from "../../helpers/overlay_chrome"
import { HEADER_GRADIENT_KEY } from "../header_gradient_preview_controller"

export default class extends BridgeComponent {
  static component = "nav-bar"

  #connectPayload = null

  connect() {
    super.connect()

    // Header colours picked in Settings (the sheet over this page) arrive as a
    // storage event: recolour the band and the native bar in place, no reload.
    this.onHeaderGradientPicked = this.#applyPickedGradient.bind(this)
    window.addEventListener("storage", this.onHeaderGradientPicked)

    // Delay send to let budget carousel's connect() settle the URL via
    // history.replaceState. Without this, the message URL (/ar/budgets)
    // mismatches the destination URL (/ar/budgets/:id) and gets ignored.
    requestAnimationFrame(() => {
      const element = this.bridgeElement
      const initial = element.bridgeAttribute("initial")

      // Unread badge — first-entry-only server seed, then localStorage owns it.
      // The very first time we ever see this device, localStorage is empty:
      // seed it from the server-rendered count. After that, localStorage is
      // the single source of truth (the unread-count controller keeps it
      // current on every read/clear) and the server attribute is ignored —
      // so a stale cached snapshot can never flip the bell back to an old
      // count on back-navigation.
      const attrBadge = element.bridgeAttribute("badge-count")
      const storedBadge = localStorage.getItem("unread_notifications_count")
      let badgeCount
      if (storedBadge !== null) {
        badgeCount = parseInt(storedBadge, 10)
      } else {
        badgeCount = parseInt(attrBadge ?? "0", 10)
        if (attrBadge !== null) localStorage.setItem("unread_notifications_count", badgeCount)
      }

      // Streak stays server-authoritative: it decays overnight with no user
      // action, so a stored number can go stale across a calendar day. Seed
      // localStorage from the server when present, fall back otherwise.
      const attrStreak = element.bridgeAttribute("streak-count")
      const streakCount = parseInt(attrStreak ?? localStorage.getItem("streak_count") ?? "0", 10)
      if (attrStreak !== null) localStorage.setItem("streak_count", streakCount)
      const settingsLabel = element.bridgeAttribute("settings-label")
      const signOutLabel = element.bridgeAttribute("sign-out-label")
      const locale = document.documentElement.lang || "en"

      // Home path the native shell caches to deep-link straight to the default
      // budget on the next cold launch (skipping the /onboarding redirect).
      // Strip the locale prefix so a language change still resolves correctly —
      // the native side re-prepends the current locale.
      const landingPathRaw = element.bridgeAttribute("landing-path")
      const landingPath = landingPathRaw ? landingPathRaw.replace(/^\/[a-z]{2}(?=\/)/, "") : ""

      // Due-today snapshot for the home-screen widget. Passed through as the
      // raw JSON string — the native side validates by decoding, then persists
      // it in the App Group container for the WidgetKit extension.
      const widgetSnapshot = element.bridgeAttribute("widget-snapshot")

      // Whether the page opens with the flexible band, so the native bar paints
      // the same gradient (false only while the account has no budgets yet).
      const headerGradient = element.bridgeAttribute("header-gradient") === "true"
      // The user's three band colours (Settings), so the bar matches the band.
      const headerGradientColors = (element.bridgeAttribute("header-gradient-colors") || "").split(",").filter(Boolean)

      this.#connectPayload = { initial, badgeCount, streakCount, settingsLabel, signOutLabel, locale, landingPath, widgetSnapshot, headerGradient, headerGradientColors }
      this.#sendConnect()

      // Bring the dim back in line with what is actually on screen. Two things
      // need this, in both directions.
      //
      // Dim: every overlay renders ABOVE this element on the dashboard, so its
      // own dim() fires before this controller exists, and on a cold launch it
      // reaches the native side before the view controller is on screen — where
      // it parks as pendingDimMessage and never gets replayed (onViewDidAppear
      // has already fired). Re-sending here is the same tick the connect
      // message above is proven to survive, and re-dimming an already-dim bar
      // is a no-op natively.
      //
      // Undim: the overlay is parented to the UINavigationController, so a dim
      // that was raised and never taken down outlives the page, the push and
      // the pop, swallowing every touch on the bar for the rest of the app
      // session — and against a near-black bar in the dark theme it is
      // invisible while it does it. The navigating CTAs each hold their visit
      // for a frame so their own undim lands (helpers/overlay_chrome), but this
      // is the net under every exit nobody enumerated.
      this.#syncDim()
    })
  }

  disconnect() {
    // The dim is a native overlay on the NAVIGATION CONTROLLER, so it outlives
    // this page and covers whatever is pushed on top of it. This component is
    // mounted on the dashboard alone (budgets/index.html.erb), so no other
    // destination can ever take it down — whoever raised it has to clear it on
    // the way out.
    //
    // A backstop, not the primary fix: the CTAs that navigate undim and defer
    // their own visit by a frame (closeChromeThenNavigate in
    // helpers/overlay_chrome), which is what makes those deterministic. This
    // catches the exits nobody enumerated — back swipe, bottom nav, a deep link
    // — where the dashboard is still the current destination as it tears down,
    // so the message routes to a delegate that still has this component.
    // Re-dimming is handled symmetrically by #syncDim() on the next connect, so
    // a popup that really is still open comes back dimmed.
    //
    // Sent BEFORE super.disconnect(), whose removePendingMessagesFor() would
    // purge it while the native adapter has not attached yet.
    this.undim()
    window.removeEventListener("storage", this.onHeaderGradientPicked)

    super.disconnect()
    this.#showWebHeader()
  }

  // FloosYo ext: dim/undim the native nav bar behind an in-page overlay (the
  // welcome popup), mirroring ButtonComponent's overlay. Handled by
  // NavBarComponent.swift.
  dim(detail = {}) {
    this.send("dim", detail)
  }

  undim() {
    this.send("undim", {})
  }

  // MARK: - Private

  // Match the native dim to whatever overlay is open right now, or take it down
  // when none is. The open overlay's own scrim is the single source of truth,
  // so the bar matches the in-page dim in both themes.
  //
  // openOverlayScrim() covers all four families (welcome, streak + goal +
  // cancel-feedback, setup-progress, rating ask) and tests :not([hidden]) —
  // which matters because every one of them is rendered into the dashboard and
  // merely hidden when closed, so their scrims sit in the DOM with nothing on
  // screen.
  //
  // Ordering with the cards themselves is safe: they all render ABOVE this
  // element in budgets/index, so Stimulus connects them first and their own
  // rAF work runs before this one.
  #syncDim() {
    const scrim = openOverlayScrim()
    if (!scrim) {
      this.undim()
      return
    }

    this.dim(scrimColorOf(scrim))
  }

  #sendConnect() {
    this.send("connect", this.#connectPayload, (message) => {
      this.#handleCallback(message)
    })
  }

  // The native side treats a repeat connect as an update (badge, chip and
  // header gradient), so re-sending it with the new colours repaints the bar.
  #applyPickedGradient(event) {
    if (event.key !== HEADER_GRADIENT_KEY || !event.newValue || !this.#connectPayload) return
    const colors = event.newValue.split(",")
    if (colors.length !== 3) return

    const band = document.querySelector(".flexible-band")
    if (band) colors.forEach((color, i) => band.style.setProperty(`--band-c${i + 1}`, color))

    this.#connectPayload = { ...this.#connectPayload, headerGradientColors: colors }
    this.#sendConnect()
  }

  #handleCallback(message) {
    const { event } = message

    if (event === "connected") {
      this.#hideWebHeader()
    } else if (event === "navigate_settings") {
      window.Turbo.visit(this.element.dataset.settingsUrl)
    } else if (event === "navigate_streak") {
      window.Turbo.visit(this.element.dataset.streakUrl)
    } else if (event === "sign_out") {
      this.#performSignOut()
    } else if (event === "notification_tapped") {
      window.Turbo.visit(this.element.dataset.notificationsUrl)
    }
  }

  #performSignOut() {
    // Try clicking the existing sign-out link in the DOM first
    const link = document.querySelector("[data-turbo-method='delete'][href*='session']")
    if (link) {
      link.click()
      return
    }

    // Fallback: programmatic form submission
    const url = this.element.dataset.signOutUrl
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const form = document.createElement("form")
    form.method = "POST"
    form.action = url

    const methodInput = document.createElement("input")
    methodInput.type = "hidden"
    methodInput.name = "_method"
    methodInput.value = "delete"
    form.appendChild(methodInput)

    if (token) {
      const tokenInput = document.createElement("input")
      tokenInput.type = "hidden"
      tokenInput.name = "authenticity_token"
      tokenInput.value = token
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.requestSubmit()
  }

  #hideWebHeader() {
    const header = document.querySelector("[data-nav-header-web]")
    if (header) {
      header.style.display = "none"
    }
  }

  #showWebHeader() {
    const header = document.querySelector("[data-nav-header-web]")
    if (header) {
      header.style.display = ""
    }
  }
}
